import React, { useEffect, useMemo, useState } from "react";
import {
  Image as ImageIcon,
  Pencil,
  Plus,
  RefreshCw,
  Trash2,
  ExternalLink,
  GripVertical,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { FieldError } from "../components/ui/field";
import { Input } from "../components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { useAuthStore } from "../context/AuthContext";
import { hasPermission } from "../lib/utils/permissions";
import useToast from "../hooks/useToast";
import apiClient from "../lib/utils/network-client";
import { DeleteConfirmationButton } from "../components/common/ConfirmationDialog";
import ImageUploadGuidance from "../components/common/ImageUploadGuidance";
import InlineFormFileUpload, {
  IMAGE_TYPES,
} from "../components/file-upload/InlineFormFileUpload";
import useDragAutoScroll from "../hooks/useDragAutoScroll";


const EMPTY_FORM = {
  title: "",
  link_url: "",
  status: "active",
  image: null,
};

const Banners = () => {
  const { user } = useAuthStore();
  const { showErrorToast, showSuccessToast } = useToast();
  const [banners, setBanners] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isReordering, setIsReordering] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingBanner, setEditingBanner] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [formErrors, setFormErrors] = useState({});
  const [previewUrl, setPreviewUrl] = useState("");
  const [draggedBannerId, setDraggedBannerId] = useState(null);
  const [dropTargetBannerId, setDropTargetBannerId] = useState(null);
  const { handleDragAutoScroll, stopDragAutoScroll } = useDragAutoScroll(
    Boolean(draggedBannerId),
    !isReordering,
  );

  const isAdmin = hasPermission(user, "banners", "list");
  const canCreate = hasPermission(user, "banners", "create");

  const dialogTitle = useMemo(
    () => (editingBanner ? "Edit Banner" : "Create Banner"),
    [editingBanner],
  );

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setFormErrors({});
    setEditingBanner(null);
    setPreviewUrl("");
  };

  const fetchBanners = async () => {
    setIsLoading(true);
    try {
      const response = await apiClient.get("/banners", {
        params: { all: true },
      });
      setBanners(response.data?.data?.banners || []);
    } catch (err) {
      showErrorToast(
        err.response?.data?.message || "Failed to load banner data",
      );
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isAdmin) {
      fetchBanners();
    }
  }, [isAdmin]);

  const openCreateDialog = () => {
    resetForm();
    setIsDialogOpen(true);
  };

  const openEditDialog = (banner) => {
    setEditingBanner(banner);
    setForm({
      title: banner.title || "",
      link_url: banner.link_url || "",
      status: banner.status || "active",
      image: null,
    });
    setPreviewUrl(banner.image_url || "");
    setIsDialogOpen(true);
  };

  const closeDialog = () => {
    setIsDialogOpen(false);
    resetForm();
  };

  const handleInputChange = (field, value) => {
    setForm((current) => ({
      ...current,
      [field]: value,
    }));
    setFormErrors((current) => ({
      ...current,
      [field]: "",
    }));
  };

  const createFormData = () => {
    const payload = new FormData();
    payload.append("title", form.title.trim());
    payload.append("link_url", form.link_url.trim());
    payload.append("status", form.status);

    if (form.image) {
      payload.append("image", form.image);
    }

    return payload;
  };

  const validateForm = () => {
    const nextErrors = {};

    if (!form.title.trim()) {
      nextErrors.title = "Banner title is required.";
    }

    if (form.link_url.trim()) {
      try {
        new URL(form.link_url.trim());
      } catch {
        nextErrors.link_url = "Enter a valid URL, including http:// or https://.";
      }
    }

    if (!editingBanner && !form.image) {
      nextErrors.image = "Banner image is required.";
    }

    setFormErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSave = async (event) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSaving(true);
    try {
      const payload = createFormData();

      if (editingBanner) {
        await apiClient.put(`/banners/${editingBanner.id}`, payload, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        showSuccessToast("Banner updated successfully");
      } else {
        await apiClient.post("/banners", payload, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        showSuccessToast("Banner created successfully");
      }

      closeDialog();
      fetchBanners();
    } catch (err) {
      showErrorToast(err.response?.data?.message || "Unable to save banner");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (banner) => {
    try {
      await apiClient.delete(`/banners/${banner.id}`);
      showSuccessToast("Banner deleted successfully");
      fetchBanners();
    } catch (err) {
      showErrorToast(err.response?.data?.message || "Unable to delete banner");
    }
  };

  const persistBannerOrder = async (orderedBanners) => {
    setIsReordering(true);
    try {
      await apiClient.put("/banners/reorder", {
        orderedItems: orderedBanners.map((banner, index) => ({
          id: banner.id,
          display_order: index + 1,
        })),
      });
      showSuccessToast("Banner order updated successfully");
      setBanners(
        orderedBanners.map((banner, index) => ({
          ...banner,
          display_order: index + 1,
        })),
      );
    } catch (err) {
      showErrorToast(
        err.response?.data?.message || "Unable to update banner order",
      );
      fetchBanners();
    } finally {
      setIsReordering(false);
      setDraggedBannerId(null);
      setDropTargetBannerId(null);
    }
  };

  const handleDragStart = (bannerId) => {
    setDraggedBannerId(bannerId);
  };

  const handleDragOverCard = (event, bannerId) => {
    event.preventDefault();

    if (draggedBannerId && draggedBannerId !== bannerId) {
      setDropTargetBannerId(bannerId);
    }
  };

  const handleDropCard = async (event, targetBannerId) => {
    event.preventDefault();
    stopDragAutoScroll();

    if (!draggedBannerId || draggedBannerId === targetBannerId) {
      setDraggedBannerId(null);
      setDropTargetBannerId(null);
      return;
    }

    const draggedIndex = banners.findIndex(
      (banner) => banner.id === draggedBannerId,
    );
    const targetIndex = banners.findIndex(
      (banner) => banner.id === targetBannerId,
    );

    if (draggedIndex < 0 || targetIndex < 0) {
      setDraggedBannerId(null);
      setDropTargetBannerId(null);
      return;
    }

    const reordered = [...banners];
    const [movedItem] = reordered.splice(draggedIndex, 1);
    reordered.splice(targetIndex, 0, movedItem);
    setBanners(reordered);
    await persistBannerOrder(reordered);
  };

  const handleDragEnd = () => {
    stopDragAutoScroll();
    setDraggedBannerId(null);
    setDropTargetBannerId(null);
  };

  if (!isAdmin) {
    return (
      <div className="rounded-lg border border-white/60 bg-white/80 p-8 shadow-sm">
        <h1 className="text-2xl font-bold text-slate-900">Banners</h1>
        <p className="mt-2 text-sm text-slate-500">
          Banner management is only available for admin users.
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-[1600px] space-y-6" onDragOver={handleDragAutoScroll}>
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900">
            Banner Management
          </h1>
          <p className="mt-1 text-sm font-medium text-slate-500">
            Manage homepage banners shown across GCS Hospital CRM.
          </p>
          <p className="mt-2 text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">
            Drag cards to reorder banners; hold near the top or bottom to auto-scroll
          </p>
        </div>

        <div className="flex gap-3">
          <Button
            type="button"
            variant="outline"
            className="rounded-lg"
            onClick={fetchBanners}
          >
            <RefreshCw className="mr-2 h-4 w-4" />
            Refresh
          </Button>
          {canCreate && (
            <Button
              type="button"
              className="rounded-lg"
              onClick={openCreateDialog}
            >
              <Plus className="mr-2 h-4 w-4" />
              Add Banner
            </Button>
          )}
        </div>
      </div>

      <div className="rounded-lg border border-white/60 bg-white/80 p-6 shadow-sm backdrop-blur-xl">
        {isLoading ? (
          <div className="py-12 text-center text-sm font-medium text-slate-500">
            Loading banners...
          </div>
        ) : banners.length === 0 ? (
          <div className="py-12 text-center">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-lg bg-slate-100 text-slate-400">
              <ImageIcon className="h-6 w-6" />
            </div>
            <h2 className="text-lg font-semibold text-slate-800">
              No banners found
            </h2>
            <p className="mt-1 text-sm text-slate-500">
              Create your first banner to populate the admin module.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-5 xl:grid-cols-2">
            {banners.map((banner) => (
              <div
                key={banner.id}
                draggable={!isReordering}
                onDragStart={() => handleDragStart(banner.id)}
                onDragOver={(event) => handleDragOverCard(event, banner.id)}
                onDrop={(event) => handleDropCard(event, banner.id)}
                onDragEnd={handleDragEnd}
                className={`overflow-hidden rounded-lg border bg-white shadow-sm transition-all duration-200 ${
                  draggedBannerId === banner.id
                    ? "scale-[0.98] opacity-60 border-primary/40"
                    : dropTargetBannerId === banner.id
                      ? "border-primary shadow-lg shadow-primary/10 ring-2 ring-primary/10"
                      : "border-slate-100"
                }`}
              >
                <div className="aspect-[26/7] bg-slate-100">
                  {banner.image_url ? (
                    <img
                      src={banner.image_url}
                      alt={banner.title}
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <div className="flex h-full items-center justify-center text-slate-400">
                      <ImageIcon className="h-8 w-8" />
                    </div>
                  )}
                </div>

                <div className="space-y-4 p-5">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex min-w-0 items-start gap-3">
                      <div className="mt-0.5 rounded-lg border border-slate-200 bg-slate-50 p-2 text-slate-400">
                        <GripVertical className="h-4 w-4" />
                      </div>
                      <div className="min-w-0">
                        <h2 className="truncate text-lg font-bold text-slate-800">
                          {banner.title}
                        </h2>
                        <p className="mt-1 text-xs font-medium text-slate-500">
                          Order: {banner.display_order ?? "-"}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {isReordering ? (
                        <span className="text-[11px] font-bold uppercase tracking-wide text-primary">
                          Saving...
                        </span>
                      ) : null}
                      <span
                        className={`rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wide ${
                          banner.status === "active"
                            ? "bg-emerald-50 text-emerald-600"
                            : "bg-slate-100 text-slate-500"
                        }`}
                      >
                        {banner.status}
                      </span>
                    </div>
                  </div>

                  <div className="space-y-1 text-sm text-slate-500">
                    <p className="line-clamp-1">
                      {banner.link_url || "No redirect link configured"}
                    </p>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    {banner.link_url ? (
                      <a
                        href={banner.link_url}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center rounded-lg border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-600 transition hover:bg-slate-50"
                      >
                        <ExternalLink className="mr-2 h-3.5 w-3.5" />
                        Open Link
                      </a>
                    ) : null}

                    <Button
                      type="button"
                      variant="outline"
                      className="rounded-lg"
                      onClick={() => openEditDialog(banner)}
                    >
                      <Pencil className="mr-2 h-4 w-4" />
                      Edit
                    </Button>
                    <DeleteConfirmationButton onConfirm={() => handleDelete(banner)}>
<Button
                      type="button"
                      variant="outline"
                      className="rounded-lg border-red-200 text-red-600 hover:bg-red-50"
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      Delete
                    </Button>
</DeleteConfirmationButton>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-2xl rounded-lg border border-white/60 bg-white p-0">
          <form onSubmit={handleSave}>
            <DialogHeader className="border-b border-slate-100 px-6 py-5">
              <DialogTitle>{dialogTitle}</DialogTitle>
              <DialogDescription>
                Upload banner artwork and configure how it appears.
              </DialogDescription>
            </DialogHeader>

            <div className="grid gap-6 px-6 py-6 md:grid-cols-[1.2fr_0.8fr]">
              <div className="space-y-4">
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-slate-700">
                    Banner Title
                  </label>
                  <Input
                    value={form.title}
                    onChange={(event) =>
                      handleInputChange("title", event.target.value)
                    }
                    placeholder="Homepage hero banner"
                    className={`rounded-lg ${formErrors.title ? "border-red-300 focus-visible:ring-red-500" : ""}`}
                  />
                  <FieldError>{formErrors.title}</FieldError>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-semibold text-slate-700">
                    Redirect URL
                  </label>
                  <Input
                    value={form.link_url}
                    onChange={(event) =>
                      handleInputChange("link_url", event.target.value)
                    }
                    placeholder="https://example.com/page"
                    className={`rounded-lg ${formErrors.link_url ? "border-red-300 focus-visible:ring-red-500" : ""}`}
                  />
                  <FieldError>{formErrors.link_url}</FieldError>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-semibold text-slate-700">
                    Status
                  </label>
                  <Select
                    value={form.status}
                    onValueChange={(value) => handleInputChange("status", value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="inactive">Inactive</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <InlineFormFileUpload
                    label="Banner Image"
                    value={form.image}
                    onChange={(file) => handleInputChange("image", file)}
                    existingFiles={previewUrl ? [previewUrl] : []}
                    accept={IMAGE_TYPES}
                    maxSizeBytes={5 * 1024 * 1024}
                    required={!editingBanner}
                    error={formErrors.image}
                    helperText="JPG, PNG, GIF or WEBP — maximum 5 MB"
                  />
                  <ImageUploadGuidance requirementKey="homeBanner" file={form.image} src={previewUrl} />
                </div>
              </div>
            </div>

            <DialogFooter className="border-t border-slate-100 px-6 py-5">
              <Button
                type="button"
                variant="outline"
                className="rounded-lg"
                onClick={closeDialog}
              >
                Cancel
              </Button>
              <Button type="submit" className="rounded-lg" disabled={isSaving}>
                {isSaving ? "Saving..." : editingBanner ? "Update Banner" : "Create Banner"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Banners;
